import Combobox from "@/components/Combobox";
import InputError from "@/components/InputError";
import SkeletonRow from "@/components/SkeletonRow";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  listHubunganKeluarga, // Pastikan ini ada di constants
  listKebutuhanKhusus,
  listLokasiKuliah,
  listSize,
} from "@/constants";
import useFilePreview from "@/hooks/useFilePreview";
import useSessionStorage from "@/hooks/useSessionStorage";
import { useUser } from "@/hooks/useUser";
import { formatDateIndonesia } from "@/lib/utils";
import {
  getBiodata,
  getListAgama,
  getListAlatTransportasi,
  getListJenisTinggal,
  getListNegara,
  getListWilayah,
} from "@/services/getData";
import {
  Agama,
  AlatTransportasi,
  BiodataSate,
  DataPendaftaran,
  DataSiswa,
  HubunganKeluargaEnum, // Pastikan ini ada di types
  JenisTinggal,
  LokasiKuliah,
  Negara,
  Option,
  Size,
  Wilayah,
} from "@/types";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertCircle,
  Briefcase,
  Camera,
  Contact2,
  Home,
  MapPin,
  Ruler,
  UploadCloud,
  User,
} from "lucide-react";
import { SetStateAction, useEffect, useRef, useState } from "react";

// --- REUSABLE COMPONENT: FORM SECTION ---
const FormSection = ({
  icon,
  title,
  children,
  className = "",
  headerClassName = "",
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  className?: string;
  headerClassName?: string;
}) => {
  return (
    <div
      className={`rounded-xl p-5 shadow-sm space-y-4 h-fit border transition-all duration-300 ${
        className ||
        "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
      }`}
    >
      <h3
        className={`font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2 border-b pb-2 ${headerClassName}`}
      >
        {icon} {title}
      </h3>
      {children}
    </div>
  );
};

export default function Biodata({
  form,
  setForm,
  error,
  file,
  setFile,
}: {
  form: BiodataSate;
  setForm: React.Dispatch<SetStateAction<BiodataSate>>;
  error: AxiosError<{
    message?: string;
    errors?: Record<string, string[]>;
  }> | null;
  file: File | null;
  setFile: React.Dispatch<SetStateAction<File | null>>;
}) {
  const { user, isPending: isPendingUser } = useUser();
  const preview = useFilePreview(file);
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const uploadRef = useRef<HTMLInputElement | null>(null);

  // --- STATE HOOKS ---
  const [openProvinsi, setOpenProvinsi] = useState(false);
  const [openProvinsiDomisili, setOpenProvinsiDomisili] = useState(false);
  const [openKabupaten, setOpenKabupaten] = useState(false);
  const [openKabupatenDomisili, setOpenKabupatenDomisili] = useState(false);
  const [openKecamatan, setOpenKecamatan] = useState(false);
  const [openKecamatanDomisili, setOpenKecamatanDomisili] = useState(false);
  const [openNegara, setOpenNegara] = useState(false);
  const [openAlatTransportasi, setOpenAlatTransportasi] = useState(false);

  const [idFeederProvinsi, setIdFeederProvinsi] = useState("");
  const [idFeederProvinsiDomisili, setIdFeederProvinsiDomisili] = useState("");
  const [idFeederKabupaten, setIdFeederKabupaten] = useState("");
  const [idFeederKabupatenDomisili, setIdFeederKabupatenDomisili] =
    useState("");

  const [searchQueryProvinsi, setSearchQueryProvinsi] = useState("");
  const [searchQueryProvinsiDomisili, setSearchQueryProvinsiDomisili] =
    useState("");
  const [searchQueryKabupaten, setSearchQueryKabupaten] = useState("");
  const [searchQueryKabupatenDomisili, setSearchQueryKabupatenDomisili] =
    useState("");
  const [searchQueryKecamatan, setSearchQueryKecamatan] = useState("");
  const [searchQueryKecamatanDomisili, setSearchQueryKecamatanDomisili] =
    useState("");

  // --- DATA FETCHING ---
  const { data: dataProvinsi, isPending: isPendingProvinsi } = useQuery({
    queryKey: ["listProvinsi", form.provinsi, searchQueryProvinsi],
    queryFn: () =>
      getListWilayah(
        10,
        "1",
        undefined,
        searchQueryProvinsi ? undefined : form.provinsi,
        searchQueryProvinsi,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
        id_feeder: `${wilayah?.id_feeder}`,
      })),
  });
  const { data: dataProvinsiDomisili, isPending: isPendingProvinsiDomisili } =
    useQuery({
      queryKey: [
        "listProvinsi",
        form.provinsi_domisili,
        searchQueryProvinsiDomisili,
      ],
      queryFn: () =>
        getListWilayah(
          10,
          "1",
          undefined,
          searchQueryProvinsiDomisili ? undefined : form.provinsi_domisili,
          searchQueryProvinsiDomisili,
        ),
      select: (data) =>
        data?.map((wilayah: Wilayah) => ({
          label: wilayah.nama_wilayah,
          value: `${wilayah.id_ref_wilayah}`,
          id_feeder: `${wilayah?.id_feeder}`,
        })),
    });
  const { data: dataKabupaten, isPending: isPendingKabupaten } = useQuery({
    queryKey: [
      "listKabupaten",
      idFeederProvinsi,
      form.kabupaten,
      searchQueryKabupaten,
    ],
    queryFn: () =>
      getListWilayah(
        10,
        undefined,
        idFeederProvinsi,
        searchQueryKabupaten ? undefined : form.kabupaten,
        searchQueryKabupaten,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
        id_feeder: `${wilayah.id_feeder}`,
      })),
    enabled: !!idFeederProvinsi,
  });
  const { data: dataKabupatenDomisili, isPending: isPendingKabupatenDomisili } =
    useQuery({
      queryKey: [
        "listKabupaten",
        idFeederProvinsiDomisili,
        form.kabupaten_domisili,
        searchQueryKabupatenDomisili,
      ],
      queryFn: () =>
        getListWilayah(
          10,
          undefined,
          idFeederProvinsiDomisili,
          searchQueryKabupatenDomisili ? undefined : form.kabupaten_domisili,
          searchQueryKabupatenDomisili,
        ),
      select: (data) =>
        data?.map((wilayah: Wilayah) => ({
          label: wilayah.nama_wilayah,
          value: `${wilayah.id_ref_wilayah}`,
          id_feeder: `${wilayah.id_feeder}`,
        })),
      enabled: !!idFeederProvinsiDomisili,
    });

  const { data: dataKecamatan, isPending: isPendingKecamatan } = useQuery({
    queryKey: [
      "listKecamatan",
      idFeederKabupaten,
      searchQueryKecamatan ? undefined : form.kecamatan,
      searchQueryKecamatan,
    ],
    queryFn: () =>
      getListWilayah(
        10,
        undefined,
        idFeederKabupaten,
        form.kecamatan,
        searchQueryKecamatan,
      ),
    select: (data) =>
      data?.map((wilayah: Wilayah) => ({
        label: wilayah.nama_wilayah,
        value: `${wilayah.id_ref_wilayah}`,
      })),
    enabled: !!idFeederKabupaten,
  });
  const { data: dataKecamatanDomisili, isPending: isPendingKecamatanDomisili } =
    useQuery({
      queryKey: [
        "listKecamatan",
        idFeederKabupatenDomisili,
        searchQueryKecamatanDomisili ? undefined : form.kecamatan_domisili,
        searchQueryKecamatanDomisili,
      ],
      queryFn: () =>
        getListWilayah(
          10,
          undefined,
          idFeederKabupatenDomisili,
          form.kecamatan_domisili,
          searchQueryKecamatanDomisili,
        ),
      select: (data) =>
        data?.map((wilayah: Wilayah) => ({
          label: wilayah.nama_wilayah,
          value: `${wilayah.id_ref_wilayah}`,
        })),
      enabled: !!idFeederKabupatenDomisili,
    });
  const { data: dataNegara } = useQuery({
    queryKey: ["listNegara"],
    queryFn: () => getListNegara(),
    select: (data) =>
      data?.map((negara: Negara) => ({
        label: negara.nama_negara,
        value: `${negara.id_ref_negara}`,
      })),
  });
  const { data: dataAgama } = useQuery({
    queryKey: ["listAgama"],
    queryFn: () => getListAgama(),
  });
  const { data: dataJenisTinggal } = useQuery({
    queryKey: ["listJenisTinggal"],
    queryFn: () => getListJenisTinggal(),
  });
  const selectedJenisTinggal =
    dataJenisTinggal &&
    dataJenisTinggal?.find((item: JenisTinggal) => {
      return item.id_ref_jenis_tinggal == form.id_ref_jenis_tinggal;
    });
  const { data: dataAlatTransportasi } = useQuery({
    queryKey: ["listAlatTransportasi"],
    queryFn: () => getListAlatTransportasi(),
    select: (data) =>
      data?.map((alatTransportasi: AlatTransportasi) => ({
        label: alatTransportasi.nama_alat_transportasi,
        value: `${alatTransportasi.id_ref_alat_transportasi}`,
      })),
  });
  const { data: dataSiswa, isPending: isPendingDataSiswa } =
    useQuery<DataSiswa>({
      queryKey: ["dataBiodata", user?.id, sessionPeriode?.id_sys_periode],
      queryFn: () => getBiodata(sessionPeriode?.id_sys_periode || ""),
      enabled: !!user?.id && !!sessionPeriode?.id_sys_periode,
    });
  const dataPendaftaran: DataPendaftaran | undefined =
    dataSiswa?.data_pendaftaran?.[0] || undefined;

  // --- Effects ---
  useEffect(() => {
    const selectedProvinsi = dataProvinsi?.find(
      (provinsi: Option) => provinsi?.value == form.provinsi,
    );
    if (selectedProvinsi) setIdFeederProvinsi(selectedProvinsi?.id_feeder);
    const selectedKabupaten = dataKabupaten?.find(
      (kabupaten: Option) => kabupaten?.value == form.kabupaten,
    );
    if (selectedKabupaten) setIdFeederKabupaten(selectedKabupaten?.id_feeder);
  }, [
    setIdFeederProvinsi,
    setIdFeederKabupaten,
    dataProvinsi,
    dataKabupaten,
    form,
  ]);
  useEffect(() => {
    if (!dataSiswa || !sessionPeriode?.id_sys_periode) return;
    setForm((prev) => ({
      ...prev,
      id_sys_periode: sessionPeriode?.id_sys_periode || "",
      ...dataSiswa,
      id_ref_negara: dataSiswa?.id_ref_negara || "103",
      pilihan_lokasi_kuliah:
        (dataSiswa.data_pendaftaran?.[0]?.data_pilihan_prodi?.[0]?.pilihan_lokasi_kuliah?.toLowerCase() as LokasiKuliah) ??
        "",
      is_alamat_sesuai_domisili:
        dataSiswa?.is_alamat_ktp_domisili_sama == 1
          ? true
          : dataSiswa?.is_alamat_ktp_domisili_sama == 0
            ? false
            : null,
    }));
  }, [dataSiswa, dataPendaftaran, setForm, sessionPeriode?.id_sys_periode]);

  useEffect(() => {
    if (!dataSiswa || !dataProvinsiDomisili || !sessionPeriode?.id_sys_periode)
      return;
    const provinsiDomisili = dataProvinsiDomisili.find(
      (prov: Option & { id_feeder: string }) => {
        return prov.value == dataSiswa.provinsi_domisili;
      },
    );
    if (provinsiDomisili) {
      setIdFeederProvinsiDomisili(provinsiDomisili?.id_feeder);
    }
  }, [dataSiswa, dataProvinsiDomisili, sessionPeriode]);

  useEffect(() => {
    if (!dataSiswa || !dataKabupatenDomisili || !sessionPeriode?.id_sys_periode)
      return;
    const kabupatenDomisili = dataKabupatenDomisili.find(
      (kab: Option & { id_feeder: string }) => {
        return kab.value == dataSiswa.kabupaten_domisili;
      },
    );
    if (kabupatenDomisili) {
      setIdFeederKabupatenDomisili(kabupatenDomisili?.id_feeder);
    }
  }, [dataSiswa, dataKabupatenDomisili, sessionPeriode]);

  const isKelasDenpasar =
    dataSiswa?.data_pendaftaran?.[0]?.periode?.sys_prodi?.[0]
      ?.is_kelas_denpasar ?? false;
  const isDataAlamatFilled =
    form.jalan !== "" &&
    form.kelurahan !== "" &&
    form.provinsi !== "" &&
    form.kabupaten !== "" &&
    form.kecamatan !== "" &&
    form.rt !== 0 &&
    form.rw !== 0 &&
    form.kode_pos !== "";
  const isDataAlamatDomisiliFilled =
    form.is_alamat_sesuai_domisili === true
      ? true
      : form.is_alamat_sesuai_domisili === false &&
        form.jalan_domisili !== "" &&
        form.kelurahan_domisili !== "" &&
        form.provinsi_domisili !== "" &&
        form.kabupaten_domisili !== "" &&
        form.kecamatan_domisili !== "";

  if (isPendingUser || isPendingDataSiswa) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-4">
          <SkeletonRow className="w-10 h-10 rounded-full" />
          <div className="space-y-2">
            <SkeletonRow className="h-6 w-48" />
            <SkeletonRow className="h-4 w-64" />
          </div>
        </div>
        {/* Foto Profil Skeleton */}
        <div className="border rounded-xl p-6 flex flex-col sm:flex-row gap-6">
          <SkeletonRow className="w-32 h-40 rounded-lg shrink-0" />
          <div className="flex-1 space-y-3 pt-2">
            <SkeletonRow className="h-5 w-32" />
            <SkeletonRow className="h-4 w-full max-w-md" />
            <SkeletonRow className="h-4 w-40" />
          </div>
        </div>
        {/* Form Grid Skeleton */}
        <div className="grid grid-cols-1 gap-6">
          <div className="border rounded-xl p-5 space-y-4">
            <SkeletonRow className="h-6 w-40 mb-4" />
            <div className="grid md:grid-cols-2 gap-4">
              <SkeletonRow className="h-10 w-full" />
              <SkeletonRow className="h-10 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      key="biodata-smbjm-cbt"
      className="space-y-6 animate-in fade-in duration-500"
    >
      {/* --- HEADER --- */}
      <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-4">
        <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">
            Lengkapi Biodata
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Isi data sesuai dokumen resmi (KTP/Ijazah).
          </p>
        </div>
      </div>

      {/* --- FOTO PROFIL --- */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col sm:flex-row items-center gap-6">
        <div
          className="relative group w-32 h-40 shrink-0 rounded-lg overflow-hidden border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 cursor-pointer hover:border-blue-500 transition-all"
          onClick={() => uploadRef.current?.click()}
        >
          {preview || form.path_foto ? (
            <img
              src={preview || form.path_foto || ""}
              alt="Foto Profil"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <Camera className="w-8 h-8 mb-2 opacity-50" />
              <span className="text-xs font-medium text-center px-2">
                Upload Foto
              </span>
            </div>
          )}
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <UploadCloud className="text-white w-6 h-6" />
          </div>
        </div>
        <div className="flex-1 space-y-2 text-center sm:text-left">
          <h3 className="font-semibold text-slate-800 dark:text-slate-200">
            Foto Profil
          </h3>
          <p className="text-sm text-slate-500">
            Unggah pas foto formal terbaru. Wajah harus terlihat jelas menghadap
            ke depan.
            <br className="hidden sm:block" /> Format: JPG/PNG, Maksimal 1MB.
          </p>
          <InputError text={error?.response?.data?.errors?.foto?.join(",")} />
        </div>
        <input
          ref={uploadRef}
          type="file"
          accept="image/png, image/jpeg, image/jpg"
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.[0]) setFile(e.target.files[0]);
          }}
        />
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* --- SECTION 1: INFORMASI UTAMA --- */}
        <FormSection
          icon={<Briefcase className="w-4 h-4 text-blue-600" />}
          title="Informasi Utama"
        >
          <div className="grid grid-cols-1 gap-4">
            {/* Read Only Fields */}
            <div className="grid md:grid-cols-2 grid-cols-1 gap-4 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-xl border border-slate-100 dark:border-slate-700/50">
              <div>
                <Label className="text-[10px] text-slate-500 uppercase">
                  Email
                </Label>
                <div className="font-mono text-xs font-medium">
                  {dataSiswa?.email || "-"}
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-slate-500 uppercase">
                  NIK / KTP
                </Label>
                <div className="font-mono text-xs font-medium">
                  {dataSiswa?.nik || "-"}
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <Label required>Nama Lengkap</Label>
              <Input
                required
                value={form.nama_lengkap || ""}
                onChange={(e) =>
                  setForm({ ...form, nama_lengkap: e.target.value })
                }
                placeholder="Sesuai Ijazah"
              />
              <InputError
                text={error?.response?.data?.errors?.nama_lengkap?.join(",")}
              />
            </div>

            <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
              <div className="space-y-1">
                <Label required>Tempat Lahir</Label>
                <Input
                  required
                  value={form.tempat_lahir || ""}
                  onChange={(e) =>
                    setForm({ ...form, tempat_lahir: e.target.value })
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.tempat_lahir?.join(",")}
                />
              </div>

              <div className="space-y-1">
                <Label required>Tanggal Lahir</Label>
                <Input
                  required
                  type="date"
                  value={form.tanggal_lahir || ""}
                  onChange={(e) =>
                    setForm({ ...form, tanggal_lahir: e.target.value })
                  }
                />
                {form.tanggal_lahir && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-1">
                    {formatDateIndonesia(form.tanggal_lahir, "medium")}
                  </p>
                )}
                <InputError
                  text={error?.response?.data?.errors?.tanggal_lahir?.join(",")}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label required>Jenis Kelamin</Label>
              <Select
                value={form.jenis_kelamin || ""}
                onValueChange={(v) =>
                  setForm({ ...form, jenis_kelamin: v as "L" | "P" })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pilih" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="L">Laki-laki</SelectItem>
                  <SelectItem value="P">Perempuan</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label required>Agama</Label>
              <Select
                value={form.id_ref_agama ? String(form.id_ref_agama) : ""}
                onValueChange={(v) => setForm({ ...form, id_ref_agama: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pilih Agama" />
                </SelectTrigger>
                <SelectContent>
                  {dataAgama?.map((a: Agama) => (
                    <SelectItem
                      key={a.id_ref_agama}
                      value={String(a.id_ref_agama)}
                    >
                      {a.nama_agama}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label required>No. Handphone (WA)</Label>
              <Input
                required
                maxLength={15}
                value={form.handphone || ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    handphone: e.target.value.replace(/\D/g, ""),
                  })
                }
              />
            </div>

            <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
              <div className="space-y-1">
                <Label>NISN</Label>
                <Input
                  disabled
                  maxLength={10}
                  value={form.nisn || ""}
                  onChange={(e) => setForm({ ...form, nisn: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>NPWP</Label>
                <Input
                  maxLength={15}
                  placeholder="NPWP"
                  value={form.npwp || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      npwp: e.target.value.replace(/\D/g, ""),
                    })
                  }
                />
              </div>
            </div>
          </div>
        </FormSection>

        {/* --- SECTION 2: DATA FISIK & PENUNJANG --- */}
        <FormSection
          icon={<Ruler className="w-4 h-4 text-purple-600" />}
          title="Data Fisik & Lainnya"
        >
          <div className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label required>Tinggi(cm)</Label>
                <Input
                  type="number"
                  value={form.tinggi_badan || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      tinggi_badan: Number(e.target.value),
                    })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label required>Berat(kg)</Label>
                <Input
                  type="number"
                  value={form.berat_badan || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      berat_badan: Number(e.target.value),
                    })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label required>Baju</Label>
                <Select
                  value={form.ukuran_baju || ""}
                  onValueChange={(v) =>
                    setForm({ ...form, ukuran_baju: v as Size })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="-" />
                  </SelectTrigger>
                  <SelectContent>
                    {listSize?.map((v, i) => (
                      <SelectItem key={i} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label required>Alat Transportasi</Label>
              <p className="italic text-muted-foreground text-sm">
                Isi dengan alat transportasi yang akan digunakan selama kuliah
              </p>
              <Combobox
                open={openAlatTransportasi}
                setOpen={setOpenAlatTransportasi}
                value={form.id_ref_alat_transportasi || ""}
                setValue={(v) =>
                  setForm({ ...form, id_ref_alat_transportasi: v })
                }
                options={dataAlatTransportasi}
                placeholder="Pilih Transportasi"
              />
            </div>

            <div className="space-y-1">
              <Label required>Kewarganegaraan</Label>
              <Combobox
                open={openNegara}
                setOpen={setOpenNegara}
                value={form.id_ref_negara || ""}
                setValue={(v) => setForm({ ...form, id_ref_negara: v })}
                options={dataNegara}
                placeholder="Pilih Negara"
              />
            </div>

            {isKelasDenpasar && (
              <div className="space-y-1">
                <Label required>Pilihan Lokasi Kuliah</Label>
                <Select
                  value={form.pilihan_lokasi_kuliah || ""}
                  onValueChange={(v) =>
                    setForm({
                      ...form,
                      pilihan_lokasi_kuliah: v as LokasiKuliah,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih Lokasi" />
                  </SelectTrigger>
                  <SelectContent>
                    {listLokasiKuliah?.map((v, i) => (
                      <SelectItem key={i} value={v.toLowerCase()}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </FormSection>
      </div>

      {/* --- SECTION 3: ALAMAT KTP --- */}
      <div className="space-y-6">
        <FormSection
          icon={<MapPin className="w-4 h-4 text-emerald-600" />}
          title="Alamat Sesuai KTP"
          className={
            isDataAlamatFilled
              ? "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
              : "bg-red-50/30 border-red-200 dark:bg-red-900/10 dark:border-red-800"
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label required>Provinsi</Label>
              <Combobox
                serverSide
                open={openProvinsi}
                setOpen={setOpenProvinsi}
                value={form.provinsi || ""}
                setValue={(v) => {
                  setForm({
                    ...form,
                    provinsi: v,
                    kabupaten: "",
                    kecamatan: "",
                  });
                  const prov = dataProvinsi?.find((p: any) => p.value == v);
                  setIdFeederProvinsi(prov?.id_feeder);
                }}
                options={dataProvinsi}
                placeholder="Pilih Provinsi"
                onChange={(e) => setSearchQueryProvinsi(e.target.value)}
                isPending={isPendingProvinsi}
                searchQueryValue={searchQueryProvinsi}
              />
            </div>

            <div className="space-y-1">
              <Label required>Kabupaten</Label>
              <Combobox
                serverSide
                open={openKabupaten}
                setOpen={setOpenKabupaten}
                value={form.kabupaten || ""}
                setValue={(v) => {
                  setForm({ ...form, kabupaten: v, kecamatan: "" });
                  const kab = dataKabupaten?.find((p: any) => p.value == v);
                  setIdFeederKabupaten(kab?.id_feeder);
                }}
                options={dataKabupaten}
                placeholder="Pilih Kabupaten"
                onChange={(e) => setSearchQueryKabupaten(e.target.value)}
                isPending={isPendingKabupaten}
                searchQueryValue={searchQueryKabupaten}
                disabled={!form.provinsi}
              />
            </div>

            <div className="space-y-1">
              <Label required>Kecamatan</Label>
              <Combobox
                serverSide
                open={openKecamatan}
                setOpen={setOpenKecamatan}
                value={form.kecamatan || ""}
                setValue={(v) => setForm({ ...form, kecamatan: v })}
                options={dataKecamatan}
                placeholder="Pilih Kecamatan"
                onChange={(e) => setSearchQueryKecamatan(e.target.value)}
                isPending={isPendingKecamatan}
                searchQueryValue={searchQueryKecamatan}
                disabled={!form.kabupaten}
              />
            </div>

            <div className="space-y-1">
              <Label required>Desa/Kelurahan</Label>
              <Input
                value={form.kelurahan || ""}
                onChange={(e) =>
                  setForm({ ...form, kelurahan: e.target.value })
                }
                placeholder="Nama Desa"
              />
            </div>

            <div className="space-y-1 md:col-span-2">
              <Label required>Jalan</Label>
              <Input
                value={form.jalan || ""}
                onChange={(e) => setForm({ ...form, jalan: e.target.value })}
                placeholder="Jalan, No. Rumah, Gang"
              />
            </div>

            <div className="grid lg:grid-cols-3 grid-cols-1 gap-4 md:col-span-2">
              <div className="space-y-1">
                <Label required>Kode Pos</Label>
                <Input
                  maxLength={5}
                  value={form.kode_pos || ""}
                  onChange={(e) =>
                    setForm({ ...form, kode_pos: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>RT</Label>
                <Input
                  type="number"
                  value={form.rt || ""}
                  onChange={(e) =>
                    setForm({ ...form, rt: Number(e.target.value) })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>RW</Label>
                <Input
                  type="number"
                  value={form.rw || ""}
                  onChange={(e) =>
                    setForm({ ...form, rw: Number(e.target.value) })
                  }
                />
              </div>
            </div>
          </div>

          <div className="space-y-1 md:col-span-2 pt-2 border-t border-slate-200 dark:border-slate-800">
            <Label required>Jenis Tinggal Saat Ini</Label>
            <Select
              value={
                form.id_ref_jenis_tinggal
                  ? String(form.id_ref_jenis_tinggal)
                  : ""
              }
              onValueChange={(v) =>
                setForm({ ...form, id_ref_jenis_tinggal: v })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Pilih Jenis Tinggal" />
              </SelectTrigger>
              <SelectContent>
                {dataJenisTinggal?.map((jt: JenisTinggal) => (
                  <SelectItem
                    key={jt.id_ref_jenis_tinggal}
                    value={String(jt.id_ref_jenis_tinggal)}
                  >
                    {jt.nama_jenis_tinggal}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedJenisTinggal?.nama_jenis_tinggal.startsWith("Panti") && (
            <div className="md:col-span-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800 space-y-1">
              <Label required>Nama Panti Asuhan</Label>
              <Input
                required
                value={form.nama_panti_asuhan || ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    nama_panti_asuhan: e.target.value,
                  })
                }
                className="bg-white dark:bg-slate-950"
              />
            </div>
          )}
        </FormSection>

        {/* --- SECTION 4: ALAMAT DOMISILI --- */}
        <FormSection
          icon={<Home className="w-4 h-4 text-amber-600" />}
          title="Alamat Domisili (Tinggal Saat Ini)"
          className={
            isDataAlamatDomisiliFilled
              ? "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
              : "bg-amber-50/30 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800"
          }
        >
          <div className="mb-4">
            <Label className="block mb-2 text-sm">
              Apakah alamat KTP sama dengan alamat tempat tinggal?
            </Label>
            <div className="flex gap-3">
              <div className="flex items-center space-x-2 p-2 border rounded bg-white dark:bg-slate-950 w-full sm:w-auto">
                <Checkbox
                  id="domisili-ya"
                  checked={form.is_alamat_sesuai_domisili === true}
                  onCheckedChange={() =>
                    setForm({ ...form, is_alamat_sesuai_domisili: true })
                  }
                />
                <label
                  htmlFor="domisili-ya"
                  className="text-sm cursor-pointer w-full"
                >
                  Ya, Sama
                </label>
              </div>
              <div className="flex items-center space-x-2 p-2 border rounded bg-white dark:bg-slate-950 w-full sm:w-auto">
                <Checkbox
                  id="domisili-tidak"
                  checked={form.is_alamat_sesuai_domisili === false}
                  onCheckedChange={() =>
                    setForm({ ...form, is_alamat_sesuai_domisili: false })
                  }
                />
                <label
                  htmlFor="domisili-tidak"
                  className="text-sm cursor-pointer w-full"
                >
                  Berbeda
                </label>
              </div>
            </div>
          </div>

          {form.is_alamat_sesuai_domisili === false && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in slide-in-from-top-2">
              <div className="space-y-1">
                <Label required>Provinsi Domisili</Label>
                <Combobox
                  serverSide
                  open={openProvinsiDomisili}
                  setOpen={setOpenProvinsiDomisili}
                  value={form.provinsi_domisili || ""}
                  setValue={(v) => {
                    setForm({
                      ...form,
                      provinsi_domisili: v,
                      kabupaten_domisili: "",
                      kecamatan_domisili: "",
                    });

                    const prov = dataProvinsiDomisili?.find(
                      (p: any) => p.value == v,
                    );

                    setIdFeederProvinsiDomisili(prov?.id_feeder);
                  }}
                  options={dataProvinsiDomisili}
                  placeholder="Pilih Provinsi"
                  onChange={(e) =>
                    setSearchQueryProvinsiDomisili(e.target.value)
                  }
                  isPending={isPendingProvinsiDomisili}
                  searchQueryValue={searchQueryProvinsiDomisili}
                />
              </div>
              <div className="space-y-1">
                <Label required>Kabupaten Domisili</Label>
                <Combobox
                  serverSide
                  open={openKabupatenDomisili}
                  setOpen={setOpenKabupatenDomisili}
                  value={form.kabupaten_domisili || ""}
                  setValue={(v) => {
                    setForm({
                      ...form,
                      kabupaten_domisili: v,
                      kecamatan_domisili: "",
                    });
                    const kab = dataKabupatenDomisili?.find(
                      (p: any) => p.value == v,
                    );
                    setIdFeederKabupatenDomisili(kab?.id_feeder);
                  }}
                  options={dataKabupatenDomisili}
                  placeholder="Pilih Kabupaten"
                  onChange={(e) =>
                    setSearchQueryKabupatenDomisili(e.target.value)
                  }
                  isPending={isPendingKabupatenDomisili}
                  searchQueryValue={searchQueryKabupatenDomisili}
                  disabled={!form.provinsi_domisili}
                />
              </div>
              <div className="space-y-1">
                <Label required>Kecamatan Domisili</Label>
                <Combobox
                  serverSide
                  open={openKecamatanDomisili}
                  setOpen={setOpenKecamatanDomisili}
                  value={form.kecamatan_domisili || ""}
                  setValue={(v) => setForm({ ...form, kecamatan_domisili: v })}
                  options={dataKecamatanDomisili}
                  placeholder="Pilih Kecamatan"
                  onChange={(e) =>
                    setSearchQueryKecamatanDomisili(e.target.value)
                  }
                  isPending={isPendingKecamatanDomisili}
                  searchQueryValue={searchQueryKecamatanDomisili}
                  disabled={!form.kabupaten_domisili}
                />
              </div>
              <div className="space-y-1">
                <Label required>Desa/Kelurahan</Label>
                <Input
                  value={form.kelurahan_domisili || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      kelurahan_domisili: e.target.value,
                    })
                  }
                  placeholder="Nama Desa"
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label required>Jalan Domisili</Label>
                <Input
                  value={form.jalan_domisili || ""}
                  onChange={(e) =>
                    setForm({ ...form, jalan_domisili: e.target.value })
                  }
                  placeholder="Nama Jalan, Kost, dll"
                />
              </div>
            </div>
          )}
        </FormSection>

        {/* --- SECTION 5: KONTAK DARURAT (NEW) --- */}
        <FormSection
          icon={<Contact2 className="w-4 h-4 text-amber-600" />}
          title="Kontak Darurat"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
            <div className="md:col-span-2 space-y-1">
              <Label required>Hubungan Kontak Darurat</Label>
              <Select
                value={form.kontak_darurat_hubungan || ""}
                onValueChange={(value) => {
                  if (!value) return;
                  setForm((prev) => ({
                    ...prev,
                    kontak_darurat_hubungan: value as HubunganKeluargaEnum,
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pilih hubungan kontak darurat" />
                </SelectTrigger>
                <SelectContent>
                  {listHubunganKeluarga?.map(
                    (option: Option, index: number) => (
                      <SelectItem key={index} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
              <InputError
                text={error?.response?.data?.errors?.kontak_darurat_hubungan?.join(
                  ",",
                )}
              />
            </div>

            <div className="space-y-1">
              <Label required>Nama Kontak Darurat</Label>
              <Input
                required
                placeholder="Nama kontak darurat..."
                value={form.kontak_darurat_nama || ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    kontak_darurat_nama: e.target.value,
                  }))
                }
              />
              <InputError
                text={error?.response?.data?.errors?.kontak_darurat_nama?.join(
                  ",",
                )}
              />
            </div>

            <div className="space-y-1">
              <Label required>No. HP Kontak Darurat</Label>
              <Input
                required
                maxLength={15}
                value={form.kontak_darurat_hp || ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    kontak_darurat_hp: e.target.value.replace(/\D/g, ""),
                  })
                }
                placeholder="08xxxxxxxxxx"
                inputMode="numeric"
              />
              <InputError
                text={error?.response?.data?.errors?.kontak_darurat_hp?.join(
                  ",",
                )}
              />
            </div>
          </div>
        </FormSection>

        {/* --- SECTION 6: KEBUTUHAN KHUSUS --- */}
        <FormSection
          icon={<AlertCircle className="w-4 h-4 text-red-500" />}
          title="Kebutuhan Khusus"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {listKebutuhanKhusus.map((value, index) => (
              <div
                key={index}
                className="flex items-center space-x-2 p-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <Checkbox
                  id={`kk-${index}`}
                  checked={form?.kebutuhan_khusus?.includes(value) || false}
                  onCheckedChange={(isChecked) => {
                    setForm((prev) => {
                      const updated = isChecked
                        ? [...(prev.kebutuhan_khusus || []), value]
                        : (prev.kebutuhan_khusus || []).filter(
                            (item) => item !== value,
                          );
                      return { ...prev, kebutuhan_khusus: updated };
                    });
                  }}
                />
                <label
                  htmlFor={`kk-${index}`}
                  className="text-xs sm:text-sm cursor-pointer select-none font-medium text-slate-700 dark:text-slate-300"
                >
                  {value}
                </label>
              </div>
            ))}
          </div>
        </FormSection>

        {/* --- ERROR SUMMARY --- */}
        {error?.response?.data?.errors &&
          (() => {
            // 1. Ekstrak dan hitung jumlah error untuk UX yang lebih informatif
            const errorList = Object.values(error.response.data.errors).flat();

            if (errorList.length === 0) return null;

            return (
              <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100 dark:bg-red-950/30 dark:border-red-900/50 shadow-sm">
                {/* Ikon di sebelah kiri untuk visual cue yang cepat */}
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />

                <div className="flex-1">
                  {/* Header dengan informasi jumlah error */}
                  <h4 className="text-sm font-bold text-red-800 dark:text-red-300 mb-2">
                    Terdapat {errorList.length} hal yang perlu diperbaiki:
                  </h4>

                  {/* List error dengan styling marker (bullet) custom */}
                  <ul className="text-sm text-red-700 dark:text-red-400 space-y-1.5 list-disc list-inside marker:text-red-400 dark:marker:text-red-600/70">
                    {errorList.map((err, i) => (
                      <li key={i} className="leading-relaxed">
                        {err}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })()}
      </div>
    </div>
  );
}
